import { useRef, useState } from "react";
import { BACKUP_MIME_TYPE } from "../../backup/backupSchema";
import { importProjectBackup } from "../../backup/importProject";
import type { PerfectDrawProject } from "../../projects/types";

interface ImportProjectButtonProps {
  allowed?: boolean;
  onBlocked?: () => void;
  onImported: (project: PerfectDrawProject) => void;
}

export function ImportProjectButton({
  allowed = true,
  onBlocked,
  onImported,
}: ImportProjectButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file: File): Promise<void> => {
    if (!allowed) {
      onBlocked?.();
      return;
    }
    setIsImporting(true);
    setError("");
    try {
      onImported(await importProjectBackup(file));
    } catch (importError) {
      console.warn(
        "Project import was rejected.",
        importError instanceof Error ? importError.name : "Error",
      );
      setError("That backup couldn’t be imported. Check the file and try again.");
    } finally {
      setIsImporting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="import-project">
      <input
        accept={`.perfectdraw,${BACKUP_MIME_TYPE}`}
        aria-label="Choose a PERFECT DRAW backup to import"
        className="sr-only"
        disabled={isImporting || !allowed}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
        }}
        ref={inputRef}
        type="file"
      />
      <button
        className="button button--secondary"
        aria-describedby={!allowed ? "import-parent-help" : undefined}
        disabled={isImporting || !allowed}
        onClick={() => {
          if (!allowed) onBlocked?.();
          else inputRef.current?.click();
        }}
        type="button"
      >
        {isImporting ? "Importing…" : "Import backup"}
      </button>
      {!allowed ? (
        <span className="sr-only" id="import-parent-help">
          Ask a parent to import a backup.
        </span>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
    </div>
  );
}
